// Referidos de un estudiante: quién lo refirió y a quién refirió él, con el
// estado de pago y del beneficio. Se usa en el detalle del estudiante de
// asesoras y de admin (Johan revisa el referido al recibir el pago).
import React from 'react'
import {
  Badge,
  Box,
  Button,
  Flex,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'

import xhr from 'xhr'

// Mismos textos que ve el estudiante en klazia.com › Referidos.
const BENEFIT = {
  sin_pago: { label: 'Aún no ha pagado', color: 'gray' },
  pendiente_reclamar: { label: 'Pagó · Pendiente por reclamar', color: 'orange' },
  aprobado: { label: 'Beneficio aprobado', color: 'green' },
  cancelado: { label: 'Cancelado', color: 'red' },
}

function BenefitBadge({ value }) {
  const info = BENEFIT[value] || { label: value, color: 'gray' }
  return (
    <Badge colorScheme={info.color} variant="subtle" textTransform="none" fontWeight="600" borderRadius="md" px={2}>
      {info.label}
    </Badge>
  )
}

function StudentLink({ student, basePath }) {
  if (!student?.id) return <Text as="span">{student?.name || 'Sin nombre'}</Text>
  return (
    <Text as={Link} to={`${basePath}/${student.id}`} fontWeight="600" color="#574feb" _hover={{ textDecoration: 'underline' }}>
      {student.name || 'Sin nombre'}
    </Text>
  )
}

export default function ReferralsTab({ studentId, basePath }) {
  const [data, setData] = React.useState(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState('')

  const load = React.useCallback(async () => {
    try {
      setLoading(true)
      setError('')
      const res = await xhr(`/referrals/by-student/${studentId}`)
      setData(res.data)
    } catch (err) {
      console.error(err)
      setError('No se pudieron cargar los referidos.')
    } finally {
      setLoading(false)
    }
  }, [studentId])

  React.useEffect(() => {
    load()
  }, [load])

  if (loading) {
    return (
      <Flex justify="center" py={8}>
        <Spinner color="#574feb" />
      </Flex>
    )
  }

  if (error) {
    return (
      <Flex direction="column" align="center" py={8} gap={3}>
        <Text color="gray.600">{error}</Text>
        <Button size="sm" variant="outline" onClick={load}>
          Reintentar
        </Button>
      </Flex>
    )
  }

  const { referredBy, referrals = [] } = data || {}
  const paidCount = referrals.filter((el) => el.benefit !== 'sin_pago').length

  return (
    <Flex direction="column" gap={5}>
      <Box border="1px solid" borderColor="gray.200" borderRadius="lg" p={4} bg="white">
        <Text fontSize="xs" color="gray.500" mb={1}>
          Referido por
        </Text>
        {referredBy ? (
          <Flex gap={2} wrap="wrap" align="center" fontSize="sm">
            <StudentLink student={referredBy.student} basePath={basePath} />
            {referredBy.student?.id && <Text color="gray.500">· Código {referredBy.student.id}</Text>}
            <Text color="gray.500">· Se registró por su link el {dayjs(referredBy.registeredAt).format('DD/MM/YYYY')}</Text>
          </Flex>
        ) : (
          <Text fontSize="sm" color="gray.500">
            No se registró con un link de referido.
          </Text>
        )}
      </Box>

      <Box>
        <Flex justify="space-between" align="baseline" mb={3} wrap="wrap" gap={2}>
          <Text fontWeight="600" color="gray.800">
            Estudiantes que refirió ({referrals.length})
          </Text>
          {referrals.length > 0 && (
            <Text fontSize="sm" color="gray.500">
              {paidCount} con pago registrado
            </Text>
          )}
        </Flex>

        {referrals.length === 0 ? (
          <Text fontSize="sm" color="gray.500" py={4}>
            Este estudiante todavía no ha referido a nadie con su link.
          </Text>
        ) : (
          <TableContainer border="1px solid" borderColor="gray.200" borderRadius="lg" bg="white">
            <Table size="sm">
              <Thead bg="gray.50">
                <Tr>
                  <Th>Referido</Th>
                  <Th>Contacto</Th>
                  <Th>Registro</Th>
                  <Th>Primer pago</Th>
                  <Th>Asesora</Th>
                  <Th>Estado</Th>
                </Tr>
              </Thead>
              <Tbody>
                {referrals.map((el) => (
                  <Tr key={el.referralId}>
                    <Td py={3}>
                      <StudentLink student={el.student} basePath={basePath} />
                      {el.student?.id && (
                        <Text fontSize="xs" color="gray.500">
                          Código {el.student.id}
                        </Text>
                      )}
                    </Td>
                    <Td>
                      <Text fontSize="sm">{el.student?.email}</Text>
                      <Text fontSize="xs" color="gray.500">
                        {el.student?.cellphone}
                      </Text>
                    </Td>
                    <Td fontSize="sm">{dayjs(el.registeredAt).format('DD/MM/YYYY')}</Td>
                    <Td fontSize="sm">{el.firstPaymentDate ? dayjs(el.firstPaymentDate).format('DD/MM/YYYY') : '—'}</Td>
                    <Td fontSize="sm">{el.student?.adviser || '—'}</Td>
                    <Td>
                      <BenefitBadge value={el.benefit} />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Flex>
  )
}
